import { z } from "zod";
const featureFlagDefinitions = {
  AgentsTab: {
    value: "agents-tab",
    description: "Makes the Agents tab visible in the navigation header top bar"
  },
  AutomatedOutreach: {
    value: "automated-outreach",
    description: "When enabled, pending participants (created via CSV or the API) will automatically be sent an SMS invite to take their interview."
  },
  AutomationReportEmailContent: {
    value: "automation-report-email-content",
    description: "Embeds screened and redacted automation report content in notification emails for companies whose link-only report email setting is disabled; while off, their emails remain link-only."
  },
  InterviewSections: {
    value: "interview-sections",
    description: "Shows interview sections cards in the job type slideover and allows sections to be added. * "
  },
  KnowledgeBase: {
    value: "knowledge-base",
    description: `Enable the display of the "Knowledge Bases" section in the settings as well as the "Knowledge bases" dropdown in the playbook editor for teammate performance knowledge base adherence scoring. Note: "Knowledge bases" and KB adherence scoring is always available/enabled for voice agents.`
  },
  HideHireTab: {
    value: "hide-hire-tab",
    description: "Despite the name, hides the whole recruiting surface for a company, not just the Hire tab: also the recruiting-flavoured settings sections and the Interview resource types. The inverse of the legacy PostHog `recruiting` flag, which was on for everyone but an exclusion list \u2014 naming the exclusion directly means companies created later keep recruiting without anyone adding a row."
  },
  Scheduling: {
    value: "scheduling",
    description: ""
  },
  TeamPerformanceTab: {
    value: "team-performance-tab",
    description: "Display the Team performance tab in the top navigation bar."
  },
  Training: {
    value: "training",
    description: ""
  },
  Reports: {
    value: "reports",
    description: ""
  },
  PerformanceReport: {
    value: "performance-report",
    description: "Show performance report card on reports page"
  },
  InsuranceReports: {
    value: "insurance-reports",
    description: "Show insurance upsell reports on reports page"
  },
  ReportsResearchAgent: {
    value: "reports-research-agent",
    description: "Enables the Copilot panel on the reports page (and hides the bottom-bar variant while it's visible)."
  },
  Topics: {
    value: "topics",
    description: "Shows the Topics view in the performance workspace, extracts interaction moments from each grading, and includes the company in the weekly per-topic analysis job. Signals are released to everyone and are not gated by this flag."
  },
  VocalIntelligence: {
    value: "vocal-intelligence",
    description: "When enabled, surfaces the vocal intelligence radar chart (authority/energy/rapport, scored 1-5) on the teammate performance overview."
  },
  CustomerExperienceScore: {
    value: "customer-experience-score",
    description: "When enabled, surfaces customer experience score values on the frontend \u2014 the per-grading 1-5 pill in the grading assessment view and gradings tables, and the per-teammate 0-100 average column in the team performance tables."
  },
  CustomerProfile: {
    value: "customer-profile",
    description: "Shows the Customers section in the Performance workspace and allows viewing its customer list."
  },
  FunnelOutcome: {
    value: "funnel-outcome",
    description: "Exposes the Funnel outcome type in the playbook outcome type picker. Playbooks that already have a Funnel outcome saved continue to show it regardless of this flag."
  },
  ZoomCcIntegration: {
    value: "zoom-cc-integration",
    description: "Shows the Zoom Contact Center card in Settings \u2192 Integrations, letting an admin connect, pause, and disconnect the call-recording sync themselves. Off by default: only contact-centre customers on Zoom should see it."
  },
  MicrosoftTeams: {
    value: "microsoft-teams",
    description: "Shows the Microsoft Teams surfaces in the admin app: the Teams card in Settings \u2192 Integrations and the Microsoft Teams delivery channel option in agent notification settings."
  },
  OutboundVoiceAgents: {
    value: "outbound-voice-agents",
    description: "Lets a company schedule outbound calls for its voice agents through the public Call Task API (POST /api/v1/call-tasks and friends). Off means the endpoints are rejected as forbidden."
  },
  AgentManagedOutreach: {
    value: "agent-managed-outreach",
    description: "Shows the 'Agent-managed outreach' toggle in an agent's Outbound calls settings."
  },
  GradingMode: {
    value: "grading-mode",
    variants: ["baseten", "openai"],
    defaultVariant: "baseten",
    description: `The provider the company grades on. "baseten" (the default) or "openai": its teammate/account assessments and the data extraction, outcome extraction, customer-experience scoring and playbook scope checks that follow run on that provider's grading queue and worker; on "openai" they are held to the shared OpenAI rate limit. Applies to jobs enqueued after it changes; jobs already queued finish on the provider they were enqueued for, so right after a change a grading can have jobs on both. HQ grading actions default their inference provider from this flag but can override it.`
  },
  NearDuplicateRecordingCheck: {
    value: "near-duplicate-recording-check",
    description: "Fingerprints every recording a company uploads through the public API and its intake integrations, indexes it, and logs the closest existing recording (a near-duplicate check, in log-only mode). Nothing is deduplicated by this flag alone; turn it on and backfill the company's history before considering the dedup flag. Off stops every part of the feature for the company. NOT TESTED AT HIGH VOLUME: before the recording is created, the upload request awaits a fingerprint Lambda (deadline 45 s for audio, 180 s for video), a ClickHouse ranking query and a Postgres recent-band read, then serialises on a per-company advisory lock behind a four-permit in-process gate. Every stage degrades to creating the recording as it does today when it fails or times out, but the latency is added to every upload and the lock makes a company's concurrent uploads wait on each other, so enabling this for a high-volume customer risks recording upload API calls timing out."
  },
  NearDuplicateRecordingDedup: {
    value: "near-duplicate-recording-dedup",
    description: "Resolves an upload whose audio near-matches an existing recording of the same teammate to that recording instead of creating a new one, the way a byte-identical re-upload already does. Implies the check flag. Enable only after the check flag's log-only period shows the expected match rate for the company. Adds no work to the upload: the fingerprint, the queries and the per-company lock all belong to the check flag and are taken whenever it is on. What this changes is the outcome, so a false match resolves an upload to the wrong recording instead of merely logging it."
  },
  TeammatePlaybookOpportunities: {
    value: "teammate-playbook-opportunities",
    description: "Highlights and filters the current week's goal and opportunity criteria in the teammate playbook table and compares scores with the team median there. The slideover layout and team example cards are available to everyone. Enables independent weekly opportunity generation, stored separately from weekly coaching goals."
  },
  TeammateCoachingTakeaways: {
    value: "teammate-coaching-takeaways",
    description: "Generates a short coaching takeaway per teammate and criterion for each saved weekly coaching opportunity, and shows the latest one in the criterion slideover. Requires teammate-playbook-opportunities, since takeaway jobs are queued by the opportunities job. Up to three agent jobs per active teammate per week; measure cost on one small company before widening."
  },
  EqualCriterionPlaybookScore: {
    value: "equal-criterion-playbook-score",
    description: "Rolls criteria up into teammate playbook and section scores by averaging each criterion over the interactions it applies to and then averaging the criteria equally, instead of averaging per-interaction scores. Affects every surface that shows a playbook or section score over more than one interaction (dashboards, reports, trends, emails, agent tools); single-interaction scores are unchanged. Safe to turn off again at any time."
  },
  CodeTools: {
    value: "code-tools",
    description: "Lets the company create and save code tools: voice agent tools written as a JavaScript script that runs in the code-tool sandbox and may call the domains the tool allows, with secrets stored encrypted per tool."
  },
  CodeToolRuntime: {
    value: "code-tool-runtime",
    variants: ["node", "rust", "node-shadowed-by-rust"],
    defaultVariant: "node",
    description: `Which runtime runs the company's code tool scripts in production. "node" (the default) runs them on code-tool-turn-function in the api's own region. "rust" runs them on code-tool-turn-rs-function, always in us-east-2. "node-shadowed-by-rust" answers on Node and invokes Rust in parallel with every turn, comparing the two per turn in the logs ("Code tool shadow turn compared"); it doubles the Lambda invocations, and since Rust's reserved concurrency is 10, a throttle there shows as rust_failed, never as a live failure. Safe to switch back at any time; it takes effect on the next tool run. The test run in the tool editor and "Try this agent" with live tool calls honor teammate rows too, so a teammate can try a runtime before their company switches.`
  },
  RecordingProcessingViaAbsurd: {
    value: "recording-processing-via-absurd",
    description: "Runs the assessment of the company's uploaded recordings (public API /upload, /upload-video and transcript-only uploads, and the S3, Genesys, Zoom CC and NICE CXone intake that share their import code) on the Absurd recording-processing worker instead of the interview-processing Lambda; the grading and other follow-up jobs stay on SQS. Recordings recorded more than 8 hours ago go to the backfill queue, as they do on SQS. Unlike SQS, recordings of the same teammate and account are no longer processed one at a time; their grading still is. Only affects recordings imported after it is turned on; safe to turn off again at any time, a task already queued still runs."
  },
  PlaybookScopeMode: {
    value: "playbook-scope-mode",
    description: `Gates the Scope tile in the playbook editor, where a playbook's scope description is edited, and whether the scope verdict is acted on. "off" hides the tile. "editor" shows it; the verdict is only recorded. "enforce" shows it and acts on the scope verdict of playbooks that have a scope description: an interaction judged out of scope gets every section marked N/A and an N/A outcome instead of being graded and extracted. A saved scope is evaluated during grading regardless of the mode. Safe to turn enforcement off again at any time; interactions already marked out of scope keep their N/A grading until they are regraded.`,
    variants: ["off", "editor", "enforce"],
    defaultVariant: "off"
  },
  VoiceAgentFunnelUi: {
    value: "voice-agent-funnel-ui",
    variants: ["hidden", "readonly", "readwrite"],
    defaultVariant: "hidden",
    description: 'The Funnel tab on dynamic voice agents and its API: the stages their interactions reach, the moments of friction in each stage, and how the top reps on the chosen playbooks handle the same moments. "hidden" (the default) hides the tab and rejects its API. "readonly" shows the tab only on a voice agent that has a funnel, without creating a funnel, editing its selling strategy or retrying a failed induction; the API answers reads and rejects writes. "readwrite" always shows the tab, and funnels can be created and edited there. How new interactions get labeled is up to voice-agent-funnel.'
  },
  VoiceAgentFunnel: {
    value: "voice-agent-funnel",
    variants: ["manual", "automated"],
    defaultVariant: "manual",
    description: 'How voice agent funnels label interactions. "manual" (the default) labels only when a person triggers it: creating a funnel (its induction and initial backfill), retrying an induction, adding a playbook, and the Label missing interactions button in HQ. "automated" also labels new interactions as they are graded, retranscribed or skipped, and runs the nightly top-rep refresh. Whether the Funnel tab shows is up to voice-agent-funnel-ui.'
  }
};
const kebabCasePattern = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const featureFlagKeySchema = z.string().min(1).max(100).regex(kebabCasePattern, "Feature flag keys are lower-case kebab-case").brand();
const featureFlagVariantSchema = z.string().min(1).max(100).regex(kebabCasePattern, "Feature flag variants are lower-case kebab-case");
function asFeatureFlagKey(key) {
  return featureFlagKeySchema.parse(key);
}
const registryFeatureFlagVariants = buildRegistryFeatureFlagVariants(Object.values(featureFlagDefinitions));
function buildRegistryFeatureFlagVariants(definitions) {
  const variantsByKey = /* @__PURE__ */ new Map();
  for (const definition of definitions) {
    if (definition.variants === void 0) {
      continue;
    }
    const { value, variants, defaultVariant } = definition;
    if (!variants.every(
      (variant) => featureFlagVariantSchema.safeParse(variant).success
    )) {
      throw new Error(
        `Feature flag "${value}" has a variant that is not lower-case kebab-case`
      );
    }
    if (!variants.includes(defaultVariant)) {
      throw new Error(
        `Feature flag "${value}" has a defaultVariant that is not one of its variants`
      );
    }
    variantsByKey.set(asFeatureFlagKey(value), { variants, defaultVariant });
  }
  return variantsByKey;
}
function findRegistryFeatureFlagVariants(key) {
  return registryFeatureFlagVariants.get(key);
}
function findFeatureFlagVariants(key) {
  const variants = registryFeatureFlagVariants.get(key);
  if (!variants) {
    throw new Error(`Feature flag "${key}" is not a variant flag`);
  }
  return variants;
}
const archivedFeatureFlagValueDefinitions = {
  "agent-automations-demo": true,
  "agent-teammate-coaching": true,
  "baseten-grading": true,
  "baseten-grading-shadow": false,
  "customer-interaction-splitting": true,
  "customer-interactions-contact-name-filter": true,
  "display-sample-tool-health-check-results": true,
  "durable-outbound-calls": true,
  forms: true,
  "genesys-integration": true,
  "live-coaching": false,
  "live-coaching-preview": false,
  "new-billing": true,
  // Replaced by `grading-mode`, which its rows were copied into; archived so
  // HQ stops offering a flag nothing reads. Builds from before the switch
  // don't know about this entry and keep reading the rows until they drain.
  "openai-grading": false,
  "playbook-feedback-review-tab": true,
  // Replaced by `playbook-scope-mode`, which their rows were copied into;
  // archived so HQ stops offering flags nothing reads. Builds from before the
  // switch don't know about these entries and keep reading the rows until they
  // drain; an admin tab still on the old bundle loses the Scope tile on its
  // next /boot until it reloads.
  "playbook-scope": false,
  "playbook-scope-enforcement": false,
  "redact-ssn": true,
  "retell-web-call-v3": true,
  "split-coaching-pipeline": false
};
const archivedFeatureFlagValues = new Map(
  Object.entries(archivedFeatureFlagValueDefinitions).map(([key, value]) => [
    asFeatureFlagKey(key),
    value
  ])
);
function getArchivedFeatureFlagValue(key) {
  return archivedFeatureFlagValues.get(key);
}
function isFeatureFlagArchived(key) {
  return archivedFeatureFlagValues.has(key);
}
const FeatureFlagKey = Object.fromEntries(
  Object.entries(featureFlagDefinitions).map(([name, { value }]) => [
    name,
    asFeatureFlagKey(value)
  ])
);
const featureFlagDescriptions = new Map(
  Object.values(featureFlagDefinitions).map((definition) => [
    asFeatureFlagKey(definition.value),
    definition.description
  ])
);
export {
  FeatureFlagKey,
  archivedFeatureFlagValues,
  asFeatureFlagKey,
  featureFlagDescriptions,
  featureFlagKeySchema,
  featureFlagVariantSchema,
  findFeatureFlagVariants,
  findRegistryFeatureFlagVariants,
  getArchivedFeatureFlagValue,
  isFeatureFlagArchived
};
//# sourceMappingURL=keys.js.map
